/**
 * Engagement Defaults admin page (SmartImprove) — Feb 2026.
 *
 * Global default settings automatically applied when a consultant
 * creates a new SmartImprove transformation engagement. Per-engagement
 * overrides still win.
 *
 * Designed as a clean enterprise settings page — no slugs, no backend
 * IDs, no technical jargon. Structured so future settings groups can
 * land alongside (report branding, narrative tone, region/customer
 * specific defaults) without restructuring this file.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { Save, Settings2, Mail, Link2 } from "lucide-react";
import { engagementDefaultsApi } from "../../lib/smartimproveAdminApi";

// ---------------------------------------------------------------------------
// Curated business-friendly taxonomies (no slugs / backend keys surfaced).
// Storage keys are preserved so existing engagement-creation logic keeps
// working unchanged. Implementation Strategy maps to the canonical
// implementation template slugs.
// ---------------------------------------------------------------------------
const ENGAGEMENT_TYPES = [
  { value: "workshop", label: "Workshop" },
  { value: "remote_review", label: "Remote Review" },
  { value: "post_go_live", label: "Post Go-Live" },
  { value: "optimization", label: "Optimization" },
];

const SEVERITIES = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const VISIBILITIES = [
  { value: "client", label: "Client" },
  { value: "demo", label: "Demo" },
];

const IMPLEMENTATION_STRATEGIES = [
  { value: "quick_win_improvement", label: "Quick Win" },
  {
    value: "targeted_operational_enhancement",
    label: "Targeted Operational Enhancement",
  },
  { value: "pilot_rollout_program", label: "Pilot + Rollout" },
  {
    value: "multi_site_warehouse_standardization",
    label: "Multi-Site Warehouse Standardization",
  },
  {
    value: "continuous_improvement_program",
    label: "Continuous Improvement Program",
  },
  {
    value: "automation_enablement_program",
    label: "Automation Enablement Program",
  },
];

const DEFAULT_DOC = {
  default_engagement_type: "workshop",
  default_severity: "medium",
  default_visibility: "client",
  default_implementation_template: "targeted_operational_enhancement",
  kickoff_email_required: true,
  auto_link_existing_customer: true,
};

function SettingsCard({ icon: Icon, title, description, children, testId }) {
  return (
    <section
      className="bg-white border border-[#E5E7EB] rounded-sm p-5 space-y-4"
      data-testid={testId}
    >
      <header className="flex items-start gap-3">
        {Icon ? (
          <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-sm bg-[#F0F7FA] text-[#025979] shrink-0">
            <Icon className="w-3.5 h-3.5" />
          </span>
        ) : null}
        <div>
          <h2
            className="text-sm font-semibold text-[#111827]"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            {title}
          </h2>
          {description ? (
            <p className="text-[11px] text-[#6B7280] mt-0.5 leading-[1.5]">
              {description}
            </p>
          ) : null}
        </div>
      </header>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

function LabeledSelect({ label, helper, value, onChange, options, testId }) {
  return (
    <div>
      <label className="block text-xs font-medium text-[#374151] mb-1">
        {label}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="rounded-sm text-xs" data-testid={testId}>
          <SelectValue placeholder="Select" />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {helper ? (
        <p className="text-[10px] text-[#94A3B8] mt-1">{helper}</p>
      ) : null}
    </div>
  );
}

function ToggleRow({ icon: Icon, label, helper, checked, onChange, testId }) {
  return (
    <label
      className="flex items-start gap-3 cursor-pointer group"
      data-testid={testId}
    >
      <span className="mt-0.5 inline-flex items-center justify-center w-7 h-7 rounded-sm bg-[#F0F7FA] text-[#025979] shrink-0">
        <Icon className="w-3.5 h-3.5" />
      </span>
      <span className="flex-1">
        <span className="block text-xs font-medium text-[#374151]">{label}</span>
        {helper ? (
          <span className="block text-[10px] text-[#94A3B8] mt-0.5 leading-[1.5]">
            {helper}
          </span>
        ) : null}
      </span>
      <input
        type="checkbox"
        checked={!!checked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 w-4 h-4 accent-[#025979] cursor-pointer"
      />
    </label>
  );
}

export default function EngagementDefaultsPage() {
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    engagementDefaultsApi
      .get()
      .then((d) => setDoc({ ...DEFAULT_DOC, ...(d || {}) }))
      .catch(() => setDoc({ ...DEFAULT_DOC }));
  }, []);

  if (!doc) return <div className="text-sm text-[#6B7280]">Loading…</div>;

  const set = (k, v) => setDoc((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const next = await engagementDefaultsApi.put(doc);
      setDoc({ ...DEFAULT_DOC, ...(next || {}) });
      toast.success("Engagement defaults saved");
    } catch {
      toast.error("Failed to save engagement defaults");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="si-engagement-defaults">
      <header className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Administration
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Engagement Defaults
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-3xl">
            Default SmartImprove engagement settings automatically applied
            when consultants create new transformation engagements.
            Individual engagements can override these.
          </p>
        </div>
        <Button
          onClick={save}
          disabled={saving}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs whitespace-nowrap"
          data-testid="si-engagement-defaults-save"
        >
          <Save className="w-3.5 h-3.5 mr-1.5" /> {saving ? "Saving…" : "Save"}
        </Button>
      </header>

      <SettingsCard
        icon={Settings2}
        title="General Engagement Settings"
        description="Defaults applied across every new SmartImprove engagement."
        testId="si-engagement-defaults-general"
      >
        <div className="grid grid-cols-2 gap-4">
          <LabeledSelect
            label="Default Engagement Type"
            value={doc.default_engagement_type || "workshop"}
            onChange={(v) => set("default_engagement_type", v)}
            options={ENGAGEMENT_TYPES}
            testId="si-engagement-defaults-type"
          />
          <LabeledSelect
            label="Default Severity"
            value={doc.default_severity || "medium"}
            onChange={(v) => set("default_severity", v)}
            options={SEVERITIES}
            testId="si-engagement-defaults-severity"
          />
          <LabeledSelect
            label="Default Visibility"
            helper="Controls whether new engagements default to client-facing or demo-mode."
            value={doc.default_visibility || "client"}
            onChange={(v) => set("default_visibility", v)}
            options={VISIBILITIES}
            testId="si-engagement-defaults-visibility"
          />
          <LabeledSelect
            label="Default Implementation Strategy"
            helper="Sets the indicative rollout playbook applied to new engagements."
            value={
              doc.default_implementation_template ||
              "targeted_operational_enhancement"
            }
            onChange={(v) => set("default_implementation_template", v)}
            options={IMPLEMENTATION_STRATEGIES}
            testId="si-engagement-defaults-strategy"
          />
        </div>
        <div className="pt-2 border-t border-[#F1F5F9] grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ToggleRow
            icon={Mail}
            label="Auto-send Kickoff Email"
            helper="Send the standard SmartImprove kickoff email when a new engagement is created."
            checked={doc.kickoff_email_required !== false}
            onChange={(v) => set("kickoff_email_required", v)}
            testId="si-engagement-defaults-kickoff"
          />
          <ToggleRow
            icon={Link2}
            label="Auto-link Existing Customer Profile"
            helper="Automatically attach the engagement to a matching customer record when one exists."
            checked={doc.auto_link_existing_customer !== false}
            onChange={(v) => set("auto_link_existing_customer", v)}
            testId="si-engagement-defaults-autolink"
          />
        </div>
      </SettingsCard>
    </div>
  );
}
